import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import { getHomeCalendar } from '../../../services/home/getHomeCalendar'
import type { HomeCalendarData } from '../../../services/home/types'
import { toSnEvents } from '../../event/utils/toSnEvent'
import type { SnEvent } from '../../event/types'

/**
 * Thirty seconds.
 *
 * Short enough that returning to the home screen picks up a game or event
 * added elsewhere, long enough that the widgets sharing this query do not each
 * trigger a request as they mount.
 */
const STALE_TIME = 30 * 1000

export interface UseHomeCalendarResult {
  /** The raw feed, for the game widgets that already read this shape. */
  data: HomeCalendarData | undefined
  /** Upcoming events, mapped onto the shape the shared event components read. */
  upcomingEvents: SnEvent[]
  isLoading: boolean
  error: Error | null
}

/**
 * The home screen's combined games + events feed.
 *
 * One request backing every home widget: the game widgets read `data` (the
 * same keys `/home/games` returned), and the upcoming-events widget reads
 * `upcomingEvents`, already adapted to `SnEvent` so it can hand them straight
 * to the shared `EventCard`.
 *
 * `isLoading` is React Query's first-load flag, so a background refetch never
 * blanks out a widget the user is already reading — the skeleton shows on cold
 * load only.
 */
export const useHomeCalendar = (): UseHomeCalendarResult => {
  'use memo'

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.homeCalendar.all,
    queryFn: getHomeCalendar,
    staleTime: STALE_TIME,
  })

  return {
    data,
    upcomingEvents: toSnEvents(data?.upcomingEvents ?? []),
    isLoading,
    error: (error as Error) ?? null,
  }
}

export default useHomeCalendar
